/**
 * Diálogo de atividade do processo: descrição e classificação do risco (chips). Grava na hora.
 */
import React, { useState } from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicActivity, ErgonomicOption } from '../../../../../types/ergonomicWorkstation';
import { dialogFieldSx } from './dialogHelpers';
import { ItemDialog } from './ItemDialog';
import { useItemSubmit } from './useItemSubmit';

type ActivityField = 'description' | 'riskClassifications';

const SERVER_LABELS: ReadonlyArray<[string, ActivityField]> = [
  ['Descrição da atividade', 'description'],
  ['Classificação do risco', 'riskClassifications'],
];

const DESCRIPTION_MAX = 1000;

interface ActivityDialogProps {
  workstationId: number;
  /** Presente ao editar; ausente ao adicionar. */
  item: ErgonomicActivity | null;
  classifications: ErgonomicOption[];
  onSaved: (message: string) => void;
  onClose: () => void;
}

export const ActivityDialog: React.FC<ActivityDialogProps> = ({
  workstationId,
  item,
  classifications,
  onSaved,
  onClose,
}) => {
  const [description, setDescription] = useState(item?.description ?? '');
  const [selected, setSelected] = useState<string[]>(item?.riskClassifications ?? []);
  const { submitting, error, errors, setErrors, run } = useItemSubmit<ActivityField>(
    SERVER_LABELS,
    'Não foi possível guardar a atividade. Tente de novo em alguns minutos.'
  );

  const toggle = (value: string) =>
    setSelected((current) => (current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]));

  const submit = async () => {
    if (description.trim() === '') {
      setErrors({ description: 'Descreva a atividade. Ex.: Fixa os componentes apertando os parafusos.' });
      return;
    }
    if (description.trim().length > DESCRIPTION_MAX) {
      setErrors({ description: `A descrição pode ter até ${DESCRIPTION_MAX} letras.` });
      return;
    }
    setErrors({});

    const saved = await run(async () => {
      const payload = { description: description.trim(), riskClassifications: selected };
      if (item) await ergonomicWorkstationService.updateActivity(workstationId, item.id, payload);
      else await ergonomicWorkstationService.createActivity(workstationId, payload);
    });
    if (saved) onSaved(item ? 'Atividade atualizada.' : 'Atividade guardada.');
  };

  return (
    <ItemDialog
      title={item ? 'Editar atividade' : 'Nova atividade'}
      submitLabel="Guardar atividade"
      submitting={submitting}
      error={error}
      onSubmit={submit}
      onClose={onClose}
    >
      <TextField
        fullWidth
        required
        multiline
        minRows={3}
        label="O que a pessoa faz neste posto"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        error={Boolean(errors.description)}
        helperText={errors.description || 'Uma atividade por vez. Ex.: Eleva a peça sobre a mesa e a posiciona.'}
        disabled={submitting}
        sx={{ mb: 3, ...dialogFieldSx }}
      />
      <Typography component="p" variant="subtitle1" id="classifications-label" sx={{ fontWeight: 700, mb: 1 }}>
        Classificação do risco
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        Marque todas que valem para esta atividade. Pode ficar sem nenhuma.
      </Typography>
      <Box role="group" aria-labelledby="classifications-label" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {classifications.map((option) => {
          const isOn = selected.includes(option.value);
          return (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              aria-pressed={isOn}
              disabled={submitting}
              onClick={() => toggle(option.value)}
              icon={isOn ? <CheckIcon /> : undefined}
              variant={isOn ? 'filled' : 'outlined'}
              sx={{ minHeight: 44, fontSize: '1rem', fontWeight: isOn ? 700 : 400, borderRadius: 22 }}
            />
          );
        })}
      </Box>
      {errors.riskClassifications && (
        <Typography color="error" variant="body1" sx={{ mt: 1 }}>
          {errors.riskClassifications}
        </Typography>
      )}
    </ItemDialog>
  );
};
